import type { DevicePlatform } from "./platform";

/** Что известно о состоянии устройства в момент, когда подключение не удалось. */
export interface ConnectFailureContext {
  permission: NotificationPermission | "unsupported";
  platform: DevicePlatform | null;
  /** ERP запущена как установленное приложение, а не во вкладке браузера. */
  installed: boolean;
}

export interface ConnectFailure {
  message: string;
  /** Нужна ли человеку пошаговая подсказка «Не видно уведомлений?». */
  showHelp: boolean;
}

/** Технические подробности для строки под сообщением; длинное не тащим на экран. */
function detailOf(error: unknown): string {
  if (!(error instanceof Error)) return "";
  const name = error.name === "Error" ? "" : `${error.name}: `;
  const text = `${name}${error.message}`.trim();

  return text === "" ? "" : ` Причина: ${text.slice(0, 140)}`;
}

/**
 * Объясняет, почему не удалось подключить устройство.
 *
 * Раньше любое сообщение со словом «permission» превращалось в «Браузер не дал
 * разрешение», хотя на iPhone разрешение уже было выдано, а сбой случался
 * на следующем шаге, при создании подписки. Человек шёл искать разрешение,
 * которое у него есть. Теперь смотрим на само состояние разрешения, а не на
 * текст ошибки, и для каждого случая называем, что делать.
 */
export function connectFailure(
  error: unknown,
  context: ConnectFailureContext,
): ConnectFailure {
  const raw = error instanceof Error ? error.message : "";

  if (raw.includes("no active Service Worker")) {
    return {
      message:
        "Браузер ещё не подготовил фоновую службу. Обновите страницу и повторите попытку — остальные уведомления работают.",
      showHelp: false,
    };
  }
  if (raw.includes("сервером пока не настроены")) {
    return { message: raw, showHelp: false };
  }

  if (context.permission === "unsupported") {
    if (context.platform === "ios" && !context.installed) {
      return {
        message:
          "На iPhone и iPad уведомления работают только в установленном приложении. Установите ERP на экран «Домой», откройте её с новой иконки и нажмите «Подключить это устройство» ещё раз.",
        showHelp: true,
      };
    }

    return {
      message:
        "Этот браузер или адрес не поддерживает уведомления при закрытой вкладке: нужен защищённый адрес (https://) и современный браузер.",
      showHelp: true,
    };
  }

  if (context.permission === "denied") {
    return {
      message:
        "Разрешение на уведомления отклонено. Верните его по шагам ниже и нажмите «Подключить это устройство» ещё раз.",
      showHelp: true,
    };
  }

  if (context.permission === "default") {
    return {
      message:
        "Запрос разрешения остался без ответа. Нажмите «Подключить это устройство» ещё раз и выберите «Разрешить».",
      showHelp: false,
    };
  }

  // Разрешение есть, значит, сбой на следующем шаге — при создании подписки.
  return {
    message: `Разрешение получено, но подключить устройство не удалось. Нажмите «Подключить это устройство» ещё раз.${detailOf(error)}`,
    showHelp: false,
  };
}
